import React, { useState, useEffect } from "react";
import { X, ChevronRight, ChevronLeft, PlayCircle } from "lucide-react";
import { useNavigate } from "react-router-dom";

const STEPS = [
  { title: "Open the demo Gmail inbox", body: "Click Conversations in the top nav to open a curated demo inbox.", to: "/app/conversations" },
  { title: "Analyze 'Partnership follow-up'", body: "Find the Emily Carter thread and click Detect. Watch how firm promises are separated from casual intent." },
  { title: "Review detected commitments", body: "Emily's revised proposal (strong), the MSA (conditional on legal), and a co-marketing one-pager will surface. Casual intent gets filtered out." },
  { title: "Accept into the ledger", body: "Only accepted items enter the ledger. Duplicates are flagged for you to keep, replace, or keep both." },
  { title: "Open the proposal commitment", body: "In the Ledger, click the Emily proposal row to open its detail view." },
  { title: "Draft a follow-up or set a reminder", body: "Try 'Draft follow-up' or set a reminder date. Every action is recorded on the timeline." },
  { title: "Analyze 'Signed proposal — attached'", body: "Back in Gmail, open the third Emily thread and Detect. The system should recognize it as possible fulfilment of the earlier proposal promise." },
  { title: "Confirm the fulfilment match", body: "Click Confirm fulfilled. The existing commitment updates, and the new message is stored as fulfilment evidence." },
  { title: "Check the updated Ledger and Impact", body: "Money moves from 'still owed' to 'marked recovered'. The Impact page reflects real activity with no fabrication.", to: "/app/impact" },
];

export const WalkthroughPanel = ({ open, onClose }) => {
  const [step, setStep] = useState(0);
  const nav = useNavigate();

  useEffect(() => {
    if (open) setStep(0);
  }, [open]);

  if (!open) return null;
  const s = STEPS[step];

  const goTo = () => {
    if (s.to) nav(s.to);
    if (step < STEPS.length - 1) setStep(step + 1);
  };

  return (
    <aside
      data-testid="walkthrough-panel"
      className="fixed top-[88px] right-6 z-40 w-[360px] bg-white border border-ledger-border shadow-lg rounded-sm animate-fadeUp"
    >
      <div className="px-5 py-4 border-b border-ledger-border flex items-center justify-between">
        <div className="flex items-center gap-2">
          <PlayCircle className="h-4 w-4 text-ledger-ink" />
          <div className="font-mono text-[11px] uppercase tracking-[0.18em] text-ledger-muted">
            Builders Fest walkthrough
          </div>
        </div>
        <button
          onClick={onClose}
          data-testid="walkthrough-close"
          className="text-ledger-muted hover:text-ledger-ink"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
      <div className="px-5 py-5">
        <div className="font-mono text-[11px] text-ledger-muted">
          Step {step + 1} of {STEPS.length}
        </div>
        <h3 className="mt-1 font-display text-xl text-ledger-ink" data-testid="walkthrough-title">
          {s.title}
        </h3>
        <p className="mt-2 text-sm text-ledger-sub leading-relaxed" data-testid="walkthrough-body">
          {s.body}
        </p>
      </div>
      <div className="px-5 py-3 border-t border-ledger-border flex items-center justify-between">
        <button
          onClick={() => setStep(Math.max(0, step - 1))}
          disabled={step === 0}
          data-testid="walkthrough-prev"
          className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full border border-ledger-border text-xs text-ledger-sub hover:border-ledger-ink hover:text-ledger-ink disabled:opacity-40"
        >
          <ChevronLeft className="h-3.5 w-3.5" /> Back
        </button>
        <div className="flex items-center gap-1">
          {STEPS.map((_, i) => (
            <div
              key={i}
              className={"h-1 w-3 rounded-full " + (i <= step ? "bg-ledger-ink" : "bg-ledger-border")}
            />
          ))}
        </div>
        <button
          onClick={goTo}
          data-testid="walkthrough-next"
          className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-ledger-ink text-white text-xs font-medium hover:bg-neutral-800"
        >
          {step === STEPS.length - 1 ? "Done" : "Next"} <ChevronRight className="h-3.5 w-3.5" />
        </button>
      </div>
    </aside>
  );
};
